"use client";

import { some } from "lodash";
import { usePathname } from "next/navigation";
import type { ReactNode } from "react";
import { LoadingScreen } from "@/components/feedback/loading-screen";
import { useLoading } from "@/context/loading-context";

interface LoadingWrapperProps {
  children: ReactNode;
}

// Routes that should have a minimal layout (no sidebar, no topbar)
const routesWithMinimalLayout = ["/cv", "/auth", "/evaluation", "/jobboard"];

export function LoadingWrapper({ children }: LoadingWrapperProps) {
  const { isLoading } = useLoading();
  const pathname = usePathname() ?? "";

  // Determine if we're in a minimal layout (full screen loading) or main layout (content area loading)
  const useMinimalLayout = some(routesWithMinimalLayout, (route) => pathname.includes(route));

  if (isLoading) {
    if (useMinimalLayout) {
      // Full screen loading for minimal layouts
      return <LoadingScreen text="Cargando aplicación..." />;
    }
    // Content area loading for layouts with sidebar/topbar
    return (
      <div className="flex flex-1 w-full items-center justify-center">
        <div className="flex items-center space-x-2">
          <svg
            className="h-8 w-8 animate-spin text-primary"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            role="img"
            aria-label="Loading spinner"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <span className="text-lg text-muted-foreground">Cargando contenido...</span>
        </div>
      </div>
    );
  }

  // If not in minimal layout, wrap children in flex container for proper layout
  if (!useMinimalLayout) {
    return <div className="flex flex-col flex-1 w-full z-0">{children}</div>;
  }

  return <>{children}</>;
}
